import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/shared/api/errors";
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useSession } from "@/features/auth";
import {
  addFavorite,
  getFavoriteIdsQueryKey,
  getMyFavoritesQueryKey,
  useAddFavorite,
  useFavoriteIds,
  useRemoveFavorite,
} from "@/shared/api/generated/favorite-controller/favorite-controller";
import type {
  FavoriteIdsResponse,
  ProductCardResponse,
} from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import { guestFavorites } from "./guestFavorites";

/**
 * Favorite product ids: the account's for a logged-in customer (one small request shared by every card), the ones
 * kept in this browser for a guest.
 */
export function useFavoriteIdSet(): ReadonlySet<string> {
  const { status } = useSession();
  const authenticated = status === "authenticated";
  const { data } = useFavoriteIds({ query: { enabled: authenticated, staleTime: 60_000 } });
  const guest = useSyncExternalStore(guestFavorites.subscribe, guestFavorites.getItems);
  return useMemo(
    () => new Set(authenticated ? (data?.fi_product_ids ?? []) : guest.map((card) => card.p_id)),
    [authenticated, data, guest],
  );
}

/** The guest's favorite cards (for the Избранное tab before login). */
export function useGuestFavorites(): readonly ProductCardResponse[] {
  return useSyncExternalStore(guestFavorites.subscribe, guestFavorites.getItems);
}

/** All favorite mutations share this key, so we can tell when the last one has finished. */
const FAVORITES_MUTATION = ["favorites"] as const;

const withId = (ids: string[], id: string) => [id, ...ids.filter((x) => x !== id)];
const withoutId = (ids: string[], id: string) => ids.filter((x) => x !== id);

/**
 * Adds or removes a favorite. The heart changes at once (optimistic update). A failed request undoes
 * only its own change (other clicks in flight keep theirs) and shows a toast; the server state is
 * refetched once the last pending change has settled. A guest's favorites change in this browser.
 */
export function useToggleFavorite(): (product: ProductCardResponse, isFavorite: boolean) => void {
  const { t } = useTranslation();
  const { status } = useSession();
  const queryClient = useQueryClient();
  const idsKey = getFavoriteIdsQueryKey();

  const update = (change: (ids: string[]) => string[]) => {
    queryClient.setQueryData<FavoriteIdsResponse>(idsKey, (data) => ({
      fi_product_ids: change(data?.fi_product_ids ?? []),
    }));
  };
  const apply = async (change: (ids: string[]) => string[]) => {
    await queryClient.cancelQueries({ queryKey: idsKey });
    update(change);
  };
  const settle = () => {
    if (queryClient.isMutating({ mutationKey: FAVORITES_MUTATION }) <= 1) {
      void queryClient.invalidateQueries({ queryKey: idsKey });
      void queryClient.invalidateQueries({ queryKey: getMyFavoritesQueryKey() });
    }
  };
  const fail = (undo: (ids: string[]) => string[]) => {
    update(undo);
    toast.error(t("product.favoriteError"));
  };

  const add = useAddFavorite({
    mutation: {
      mutationKey: FAVORITES_MUTATION,
      onMutate: ({ productId }) => apply((ids) => withId(ids, productId)),
      onError: (_error, { productId }) => {
        fail((ids) => withoutId(ids, productId));
      },
      onSettled: settle,
    },
  });
  const remove = useRemoveFavorite({
    mutation: {
      mutationKey: FAVORITES_MUTATION,
      onMutate: ({ productId }) => apply((ids) => withoutId(ids, productId)),
      onError: (_error, { productId }) => {
        fail((ids) => withId(ids, productId));
      },
      onSettled: settle,
    },
  });

  return (product, isFavorite) => {
    if (status !== "authenticated") {
      guestFavorites.toggle(product);
      return;
    }
    (isFavorite ? remove : add).mutate({ productId: product.p_id });
  };
}

/**
 * After login, moves the guest's favorites from this browser into the account (once per login), so nothing the
 * visitor saved is lost.
 */
export function useMergeGuestFavoritesOnLogin(): void {
  const { t } = useTranslation();
  const { status } = useSession();
  const queryClient = useQueryClient();
  const attempted = useRef(false);

  useEffect(() => {
    if (status !== "authenticated") {
      attempted.current = false;
      return;
    }
    const cards = guestFavorites.getItems();
    if (cards.length === 0 || attempted.current) return;
    attempted.current = true;
    // oldest first, so the account keeps the guest's order (newest on top)
    const ids = [...cards].reverse().map((card) => card.p_id);
    void (async () => {
      // one product at a time (keeps the order); a product the server refuses for good (gone from the catalog)
      // is dropped, only a network or server failure keeps it for the next login
      const done: string[] = [];
      for (const productId of ids) {
        try {
          await addFavorite(productId);
          done.push(productId);
        } catch (error) {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500)
            done.push(productId);
        }
      }
      guestFavorites.removeMany(done);
      if (done.length < ids.length) toast.error(t("product.favoriteError"));
      void queryClient.invalidateQueries({ queryKey: getFavoriteIdsQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getMyFavoritesQueryKey() });
    })();
  }, [status, queryClient, t]);
}
